import { useState, useTransition } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import {
  useLazyReadTenantForSuperTenantQuery,
  useLoginTenantForSuperTenantMutation,
} from '@/backoffice/redux/api/backoffice/backoffice-api';
import { auditApi } from '@/shared/redux/api/audit/audit-api';
import { setBackofficeTenantIdToken } from '@/shared/redux/api/auth/auth-api';
import { billingApi } from '@/shared/redux/api/billing/billing-api';
import { identApi } from '@/shared/redux/api/ident/ident-api';
import { useAppDispatch } from '@/shared/redux/hooks/hooks';
import { showToastError } from '@/shared/utils/toast';

export const useLoginTenant = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isLoggingIn, startLogin] = useTransition();
  const [isError, setIsError] = useState(false);
  const [readTenant] = useLazyReadTenantForSuperTenantQuery();
  const [loginTenantForSuperTenant] = useLoginTenantForSuperTenantMutation();

  const loginTenant = (tenantId?: string) => {
    startLogin(async () => {
      dispatch(identApi.util.resetApiState());
      dispatch(auditApi.util.resetApiState());
      dispatch(billingApi.util.resetApiState());
      setIsError(false);

      if (!tenantId) {
        return;
      }

      // Tenant isn't readable for this super-tenant (no access / different reseller);
      // the backend message is suppressed in error-handlers, so surface the boundary.
      const tenantResponse = await readTenant({ tenantId })
        .unwrap()
        .catch(() => {
          setIsError(true);
          return undefined;
        });

      if (!tenantResponse) {
        return;
      }

      const isTenantActive = tenantResponse?.readTenant?.isActive;

      if (!isTenantActive) {
        navigate('/');
        showToastError(t('backoffice:tenant_inactive'));
        return;
      }

      const storedToken = localStorage.getItem(`backoffice:${tenantId}`);
      const decodedToken = storedToken
        ? JSON.parse(atob(storedToken.split('.')[1]))
        : null;
      const isTokenValid = decodedToken && decodedToken.exp * 1000 > Date.now();

      if (isTokenValid) {
        dispatch(setBackofficeTenantIdToken(storedToken));
        return;
      }

      try {
        const response = await loginTenantForSuperTenant({
          tenantId,
        }).unwrap();
        const newIdToken = response?.loginTenant.idToken;
        if (!newIdToken) {
          throw new Error('No id token returned');
        }
        localStorage.setItem(`backoffice:${tenantId}`, newIdToken);
        dispatch(setBackofficeTenantIdToken(newIdToken));
      } catch {
        localStorage.removeItem(`backoffice:${tenantId}`);
        dispatch(setBackofficeTenantIdToken(null));
        setIsError(true);
      }
    });
  };

  return {
    loginTenant,
    isLoggingIn,
    isError,
  };
};
